import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, MapPin, Calendar, Users, Clock, Trophy, DollarSign, Zap, HelpCircle } from "lucide-react";
import ShareMenu from "@/components/ShareMenu";
import SponsorStrip from "@/components/SponsorStrip";
import PsiMoversBoard from "@/components/PsiMoversBoard";
import { fmtDate, isMoneyball, eventUrl } from "@/pages/PublicEvents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = "https://www.picklebullpen.com";

const setMeta = (attr, key, content) => {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

const buildFaq = (t, past) => [
  { q: `How do I register for ${t.name}?`,
    a: `Create a free Pickle Bullpen player profile, open the Tournaments tab and tap Register on ${t.name}. Registration is ${t.status === "open" ? "open now" : past ? "closed — this event is complete" : "opening soon"}${t.entry_fee > 0 ? ` and entry is $${t.entry_fee}` : " and entry is free"}.` },
  { q: `Where is ${t.name} played?`,
    a: `${t.name} is played at ${t.location}${t.venue ? ` (${t.venue})` : ""} on ${fmtDate(t.date, t.end_date)}${t.start_time ? ` starting at ${t.start_time}` : ""}.` },
  { q: `What skill divisions can I enter?`,
    a: (t.levels || []).length > 0
      ? `This pickleball tournament offers ${(t.levels || []).join(", ")} divisions across ${(t.event_types || []).join(", ")}. Your Pickle Bullpen PSI (Player Skill Index) rating guides you to the right division.`
      : `Divisions are set by the tournament director. Your Pickle Bullpen PSI rating guides you to the right division.` },
  { q: `Do matches count toward my pickleball rating?`,
    a: `Yes — every match feeds your Player Skill Index (PSI) and PBR ranking points, Pickle Bullpen's performance rating system. Podium finishes help unlock State and National level events.` },
];

export default function PublicEventDetail() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    const rawId = id.slice(-24);
    axios.get(`${API}/public/events/${rawId}`).then(({ data }) => {
      setT(data);
      const past = data.status === "completed" || data.status === "complete";
      const loc = data.location || "Florida";
      const title = `${data.name} — Pickleball Tournament in ${loc} · ${fmtDate(data.date, data.end_date)} | Pickle Bullpen`;
      const desc = `${data.name}: ${past ? "results, champions and recap" : "register now"} — pickleball tournament at ${loc}${data.venue ? ` (${data.venue})` : ""} on ${fmtDate(data.date, data.end_date)}. ${(data.levels || []).join(", ")} divisions · ${(data.event_types || []).join(", ")}${data.entry_fee > 0 ? ` · $${data.entry_fee} entry` : " · Free entry"}. Every match counts toward your PSI rating.`;
      const canonical = `${SITE}${eventUrl(data)}`;
      document.title = title;
      setMeta("name", "description", desc);
      setMeta("name", "keywords", `pickleball tournament ${loc}, ${data.name}, pickleball ${loc}, pickleball events near me, ${(data.event_types || []).join(" pickleball, ")} pickleball, pickleball brackets, Pickle Bullpen`);
      setMeta("property", "og:title", title);
      setMeta("property", "og:description", desc);
      setMeta("property", "og:url", canonical);
      setMeta("property", "og:type", "website");
      if (data.image) setMeta("property", "og:image", data.image);
      setMeta("name", "twitter:title", title);
      setMeta("name", "twitter:description", desc);
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
      link.setAttribute("href", canonical);
      // Structured data: SportsEvent + FAQPage + BreadcrumbList
      const ld = [
        { "@context": "https://schema.org", "@type": "SportsEvent", name: data.name, sport: "Pickleball",
          startDate: data.start_time ? `${data.date}T${data.start_time}:00` : data.date,
          ...(data.end_date ? { endDate: data.end_date } : {}),
          eventStatus: "https://schema.org/EventScheduled",
          eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
          location: { "@type": "Place", name: data.venue || data.location,
            address: { "@type": "PostalAddress", addressLocality: data.location || "Bradenton", addressRegion: "FL", addressCountry: "US" } },
          image: data.image || `${SITE}/og-image.jpg`, url: canonical,
          description: desc,
          organizer: { "@type": "Organization", name: "Pickle Bullpen", url: SITE },
          offers: { "@type": "Offer", url: `${SITE}/auth?mode=register`, price: String(data.entry_fee || 0), priceCurrency: "USD",
            availability: data.status === "open" ? "https://schema.org/InStock" : "https://schema.org/SoldOut" } },
        { "@context": "https://schema.org", "@type": "FAQPage",
          mainEntity: buildFaq(data, past).map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
        { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
          { "@type": "ListItem", position: 1, name: "Pickle Bullpen", item: SITE },
          { "@type": "ListItem", position: 2, name: "Pickleball Tournaments & Events", item: `${SITE}/events` },
          { "@type": "ListItem", position: 3, name: data.name, item: canonical } ] },
      ];
      document.querySelectorAll('script[data-seo="event"]').forEach((s) => s.remove());
      ld.forEach((obj) => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.dataset.seo = "event";
        s.textContent = JSON.stringify(obj);
        document.head.appendChild(s);
      });
    }).catch(() => setMissing(true));
    return () => document.querySelectorAll('script[data-seo="event"]').forEach((s) => s.remove());
  }, [id]);

  if (missing) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center gap-4" data-testid="event-not-found">
      <p className="uppercase tracking-widest text-zinc-500">Event not found</p>
      <Link to="/events" className="text-lime-400 text-sm underline">See all events</Link>
    </div>
  );
  if (!t) return null;
  const past = t.status === "completed" || t.status === "complete";
  const champs = Object.entries(t.champions || {});
  const faq = buildFaq(t, past);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-6 py-10" data-testid="public-event-detail">
      <div className="max-w-3xl mx-auto">
        <nav className="mb-8 text-xs uppercase tracking-widest text-zinc-500" data-testid="event-breadcrumbs">
          <Link to="/" className="hover:text-lime-400 transition-colors">Pickle Bullpen</Link>
          <span className="mx-2 text-zinc-700">/</span>
          <Link to="/events" data-testid="event-back-link" className="hover:text-lime-400 transition-colors inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> All Events</Link>
        </nav>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {isMoneyball(t) && <span className="text-[10px] font-black uppercase tracking-widest bg-amber-400 text-black px-2.5 py-1">💰 Moneyball Event</span>}
          <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 border ${past ? "border-zinc-700 text-zinc-500" : "border-lime-400/50 text-lime-300"}`}>
            {past ? "Completed" : t.status === "open" ? "Registration Open" : t.status}
          </span>
        </div>
        <div className="flex items-start gap-4">
          {t.logo && <img src={t.logo} alt={`${t.name} logo`} className="w-16 h-16 object-contain shrink-0 mt-1" data-testid="event-logo" />}
          <div>
            <h1 className="font-display font-black uppercase leading-[0.95] text-4xl sm:text-5xl mb-2" data-testid="event-title">{t.name}</h1>
            <p className="text-lime-300/90 font-mono uppercase tracking-[0.2em] text-xs mb-6" data-testid="event-subtitle">
              Pickleball Tournament in {t.location || "Florida"} · {fmtDate(t.date, t.end_date)}
            </p>
          </div>
        </div>
        {t.image && <img src={t.image} alt={`${t.name} — pickleball tournament in ${t.location}`} className="w-full max-h-80 object-cover border border-zinc-800 mb-6" />}

        <div className="grid sm:grid-cols-2 gap-3 mb-8">
          {[[Calendar, "Date", fmtDate(t.date, t.end_date)],
            [Clock, "Time", t.start_time ? `${t.start_time}${t.end_time ? ` – ${t.end_time}` : ""}` : "TBA"],
            [MapPin, "Location", `${t.location}${t.venue ? ` (${t.venue})` : ""}`],
            [DollarSign, "Entry", (t.effective_entry_fee ?? t.entry_fee) > 0 ? `$${t.effective_entry_fee ?? t.entry_fee}${t.early_bird_active ? " Early Bird" : ""}${t.entry_fee_coins ? ` or ${t.entry_fee_coins} BLC` : ""}` : "Free"],
            [Users, "Registered", `${t.entry_count} / ${t.capacity} players`],
            [Trophy, "Format", `${t.format} · ${(t.event_types || []).join(", ")}`]].map(([Icon, l, v]) => (
            <div key={l} className="flex items-start gap-3 border border-zinc-800 bg-white/[0.03] p-4">
              <Icon className="w-4 h-4 text-lime-400 mt-0.5 shrink-0" />
              <div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">{l}</div>
                <div className="text-sm font-semibold">{v}</div>
              </div>
            </div>
          ))}
        </div>

        {t.event_config ? (
          <div className="mb-8" data-testid="event-divisions">
            <h2 className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Divisions by Event</h2>
            <div className="space-y-2">
              {(t.event_types || []).map((ev) => {
                const c = t.event_config[ev] || {};
                const lv = (c.levels || []).includes("All") ? ["Open skill"] : c.levels || [];
                return (
                  <div key={ev} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`event-division-${ev.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <span className="font-bold uppercase tracking-wider text-white min-w-[140px]">{ev}</span>
                    {lv.map((l) => <span key={l} className="px-2 py-0.5 rounded-full bg-white/10 border border-white/15 text-[10px] font-bold uppercase tracking-widest">{l}</span>)}
                    {(c.age_brackets || []).map((a) => <span key={a} className="px-2 py-0.5 rounded-full bg-cyan-400/10 border border-cyan-400/30 text-cyan-300 text-[10px] font-bold tracking-widest">{a}</span>)}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (t.levels || []).length > 0 && (
          <div className="mb-8">
            <h2 className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Skill Divisions</h2>
            <div className="flex flex-wrap gap-2">
              {t.levels.map((l) => (
                <span key={l} className="px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs font-bold uppercase tracking-widest">{l}</span>
              ))}
            </div>
          </div>
        )}

        {(t.sponsors || []).length > 0 && (
          <div className="mb-8 border border-pb-border p-4" data-testid="event-sponsors">
            <SponsorStrip sponsors={t.sponsors} tid={t.id} />
          </div>
        )}

        {(t.prize_pools || []).length > 0 && (
          <div className="mb-8 border border-amber-400/40 bg-amber-400/5 p-5" data-testid="event-prize-pools">
            <h2 className="flex items-center gap-2 font-display font-extrabold uppercase text-amber-400 text-lg mb-1">
              <DollarSign className="w-4 h-4" /> Moneyball Purse
            </h2>
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-4">The purse grows as more players register</p>
            {Object.keys(t.team_dupr_caps || {}).length > 0 && (
              <div className="mb-4 -mt-2 flex flex-wrap gap-2" data-testid="event-team-caps">
                {Object.entries(t.team_dupr_caps).map(([et, cap]) => (
                  <span key={et} className="text-xs font-bold uppercase tracking-widest text-amber-300 border border-amber-400/30 bg-amber-400/5 px-3 py-1.5">
                    {et}: Team DUPR {cap} · PSI {(t.team_psi_caps || {})[et]}
                  </span>
                ))}
              </div>
            )}
            <div className="grid sm:grid-cols-3 gap-3">
              {[...t.prize_pools].sort((a, b) => a.players - b.players).map((p) => {
                const unlocked = (t.entry_count || 0) >= p.players;
                return (
                  <div key={p.players} className={`border p-4 ${unlocked ? "border-amber-400 bg-amber-400/10" : "border-zinc-800 bg-white/[0.03]"}`} data-testid={`event-purse-tier-${p.players}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-black uppercase tracking-widest">{p.players} Players</span>
                      <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 ${unlocked ? "bg-amber-400 text-black" : "border border-zinc-700 text-zinc-500"}`}>
                        {unlocked ? "Unlocked" : `${Math.max(0, p.players - (t.entry_count || 0))} to go`}
                      </span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between"><span className="text-zinc-500 text-[10px] uppercase tracking-widest">1st Place</span><span className="font-bold text-amber-300">${p.first}</span></div>
                      <div className="flex justify-between"><span className="text-zinc-500 text-[10px] uppercase tracking-widest">2nd Place</span><span className="font-bold">${p.second}</span></div>
                      <div className="flex justify-between"><span className="text-zinc-500 text-[10px] uppercase tracking-widest">3rd Place</span><span className="font-bold">${p.third}</span></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {t.description && <p className="text-zinc-300 leading-relaxed mb-8 whitespace-pre-line">{t.description}</p>}

        {champs.length > 0 && (
          <div className="mb-8 border border-amber-400/40 bg-amber-400/5 p-5" data-testid="event-champions">
            <h2 className="flex items-center gap-2 font-display font-extrabold uppercase text-amber-400 text-lg mb-3">
              <Trophy className="w-4 h-4" /> Champions
            </h2>
            <div className="space-y-1.5 text-sm">
              {champs.map(([div, name]) => (
                <div key={div}><span className="text-zinc-500 uppercase text-[10px] tracking-widest mr-2">{div}</span><span className="font-bold">{typeof name === "string" ? name : name?.champion_name || name?.name || ""}</span></div>
              ))}
            </div>
            <Link to={`${eventUrl(t)}/recap`} data-testid="event-recap-btn"
              className="inline-flex items-center gap-1.5 mt-4 text-xs font-bold uppercase tracking-widest text-amber-300 border border-amber-400/50 px-4 py-2 hover:bg-amber-400 hover:text-black transition-colors">
              Read the Full Recap — Results & Medal Scores →
            </Link>
          </div>
        )}

        {past && t.psi_movers?.top3?.length > 0 && (
          <div className="mb-8"><PsiMoversBoard data={{ ...t.psi_movers, final: true }} title="Top 3 PSI Movers of the Event" testid="event-psi-movers" /></div>
        )}

        {/* SEO: why play with the Bullpen */}
        <div className="mb-8 border border-zinc-800 bg-white/[0.02] p-5" data-testid="event-why-bullpen">
          <h2 className="flex items-center gap-2 font-display font-extrabold uppercase text-lime-300 text-lg mb-2">
            <Zap className="w-4 h-4" /> Every Match Counts
          </h2>
          <p className="text-sm text-zinc-400 leading-relaxed">
            {t.name} runs on the Pickle Bullpen performance ecosystem — live brackets, court assignments on a big-screen
            court board, TD-approved scores and instant results. Every match you play feeds your <strong className="text-zinc-200">Player Skill
            Index (PSI)</strong> and <strong className="text-zinc-200">PBR ranking points</strong>, medals hit your permanent record the moment the
            tournament ends, and podium finishes unlock entry to State and National level pickleball events.
          </p>
        </div>

        {/* SEO: FAQ (matches FAQPage structured data) */}
        <div className="mb-8" data-testid="event-faq">
          <h2 className="flex items-center gap-2 font-display font-extrabold uppercase text-lg mb-4">
            <HelpCircle className="w-4 h-4 text-lime-400" /> {t.name} — FAQ
          </h2>
          <div className="space-y-3">
            {faq.map((f, i) => (
              <details key={i} className="border border-zinc-800 bg-white/[0.02] group" data-testid={`event-faq-${i}`}>
                <summary className="cursor-pointer list-none px-4 py-3 text-sm font-bold flex items-center justify-between">
                  {f.q}<span className="text-zinc-600 group-open:rotate-45 transition-transform text-lg leading-none">+</span>
                </summary>
                <p className="px-4 pb-4 text-sm text-zinc-400 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 border-t border-zinc-900 pt-8">
          {!past && (
            <Link to="/auth?mode=register" data-testid="event-register-btn"
              className="inline-flex items-center gap-2 rounded-full bg-lime-400 text-black font-display font-bold uppercase tracking-wider px-8 py-3 hover:bg-lime-300 transition-colors">
              Register Now{t.entry_fee > 0 ? ` · $${t.entry_fee}` : " · Free"}
            </Link>
          )}
          <div className="border border-zinc-800 px-4 py-2.5">
            <ShareMenu url={`${process.env.REACT_APP_BACKEND_URL}/api/share/events/${t.id}`} image={t.image}
              text={`${t.name} — ${fmtDate(t.date, t.end_date)} at ${t.location}. Register with Pickle Bullpen!`}
              testId="event-share" label="Share this event" />
          </div>
        </div>

        {/* SEO: internal links */}
        <div className="mt-10 pt-6 border-t border-zinc-900 text-xs text-zinc-500 flex flex-wrap gap-x-5 gap-y-2" data-testid="event-internal-links">
          <Link to="/events" className="hover:text-lime-400 transition-colors">All Pickleball Tournaments</Link>
          <Link to="/gallery" className="hover:text-lime-400 transition-colors">Tournament Photo Gallery</Link>
          <Link to="/court-board" className="hover:text-lime-400 transition-colors">Live Court Board</Link>
          <Link to="/faq" className="hover:text-lime-400 transition-colors">How PSI Ratings Work</Link>
          <Link to="/auth?mode=register" className="hover:text-lime-400 transition-colors">Create a Free Player Profile</Link>
        </div>
      </div>
    </div>
  );
}
